// Core domain types shared between the API, dashboard and player.
// These describe the wire/JSON shape — Prisma models mirror them on the server.
/** The 12 built-in menu designs. */
export const MENU_TEMPLATES = [
    { id: 'classic-light', label: 'Klasik Açık', group: 'Sade', bg: '#fbfaf7', text: '#1a1a1a', muted: '#6b7280', accent: '#b45309', font: 'serif', divider: 'dots', upper: true },
    { id: 'classic-dark', label: 'Klasik Koyu', group: 'Sade', bg: 'radial-gradient(120% 120% at 50% 0%, #161b2e 0%, #0b1020 70%)', text: '#f4f6fb', muted: '#94a3b8', accent: '#fbbf24', font: 'sans', divider: 'dots', upper: true },
    { id: 'minimal-mono', label: 'Minimal', group: 'Sade', bg: '#ffffff', text: '#111827', muted: '#9ca3af', accent: '#111827', font: 'sans', divider: 'line', upper: false },
    { id: 'coffee-warm', label: 'Sıcak Kahve', group: 'Kafe', bg: 'linear-gradient(150deg, #3b2417 0%, #1f140c 100%)', text: '#f5e9da', muted: '#c9a98a', accent: '#e0a96d', font: 'serif', divider: 'dots', upper: true },
    { id: 'chalkboard', label: 'Kara Tahta', group: 'Kafe', bg: '#1c2321', text: '#f3f4f1', muted: '#b8c0ba', accent: '#f4d35e', font: 'mono', divider: 'none', upper: true },
    { id: 'pastel-bakery', label: 'Pastane Pastel', group: 'Pastane', bg: 'linear-gradient(160deg, #fdf2f8 0%, #fae8ff 100%)', text: '#6b264f', muted: '#9d6b86', accent: '#db2777', font: 'rounded', divider: 'dots', upper: false },
    { id: 'fastfood-bold', label: 'Fast Food', group: 'Fast Food', bg: 'linear-gradient(145deg, #e11d48 0%, #b91c1c 100%)', text: '#ffffff', muted: '#ffe4e6', accent: '#fde047', font: 'condensed', divider: 'none', upper: true },
    { id: 'bistro-green', label: 'Bistro Yeşil', group: 'Restoran', bg: '#14241c', text: '#f0ece1', muted: '#b7c4ad', accent: '#d4af37', font: 'serif', divider: 'line', upper: true },
    { id: 'mediterranean', label: 'Akdeniz', group: 'Restoran', bg: 'linear-gradient(150deg, #0ea5e9 0%, #0369a1 100%)', text: '#ffffff', muted: '#dbeafe', accent: '#fde68a', font: 'sans', divider: 'dots', upper: true },
    { id: 'luxe-goldblack', label: 'Lüks Altın-Siyah', group: 'Şık', bg: '#0a0a0a', text: '#f5e6c8', muted: '#a99878', accent: '#d4af37', font: 'serif', divider: 'line', upper: true },
    { id: 'brand-violet', label: 'Marka Mor', group: 'Şık', bg: 'linear-gradient(140deg, #4f46e5 0%, #6d28d9 100%)', text: '#ffffff', muted: 'rgba(255,255,255,.72)', accent: '#fde68a', font: 'sans', divider: 'line', upper: true },
    { id: 'night-neon', label: 'Neon Gece', group: 'Bar', bg: '#0b0a1f', text: '#e9e7ff', muted: '#9d9bc7', accent: '#22d3ee', font: 'sans', divider: 'line', upper: true },
];
/** Map a template/legacy-theme key to its template (always returns one). */
export function getMenuTemplate(key) {
    if (key) {
        const exact = MENU_TEMPLATES.find((t) => t.id === key);
        if (exact)
            return exact;
        // Legacy theme → template fallback.
        const legacy = {
            light: 'classic-light',
            dark: 'classic-dark',
            brand: 'brand-violet',
        };
        const mapped = MENU_TEMPLATES.find((t) => t.id === legacy[key]);
        if (mapped)
            return mapped;
    }
    return MENU_TEMPLATES[0];
}
/** Concrete font-family stack for a template font (no external fonts → works
 *  offline on low-end players). */
export function menuFontStack(font) {
    switch (font) {
        case 'serif':
            return "Georgia, 'Times New Roman', serif";
        case 'condensed':
            return "'Arial Narrow', 'Helvetica Neue', Impact, sans-serif";
        case 'mono':
            return "'Courier New', ui-monospace, monospace";
        case 'rounded':
            return "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif";
        default:
            return "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    }
}
/** Higher number wins. Player always plays the highest active priority. */
export const BroadcastPriority = {
    emergency: 100,
    instant: 50,
};
export const SECTOR_LABELS = {
    restaurant: 'Restoran',
    cafe: 'Kafe',
    municipality: 'Belediye',
    hospital: 'Hastane',
    retail: 'AVM / Mağaza',
    hotel: 'Otel',
    generic: 'Genel / Diğer',
};
export const MODULE_LABELS = {
    menuboard: 'Menüboard',
    pricePromo: 'Kampanya / Fiyat',
    prayerTimes: 'Namaz Vakitleri',
    disasterAlert: 'AFAD / Afet Uyarısı',
    queueBoard: 'Sıra / Yönlendirme',
    hotelGuest: 'Misafir Karşılama',
    ai: 'Yapay Zeka (eklenti)',
};
/**
 * Default modules each sector sees out of the box. The `ai` add-on is NOT a
 * sector default — it is a paid extra a super-admin enables per org via
 * `moduleOverrides.ai = true` after purchase (metered AI spend, cost control).
 */
export const SECTOR_MODULES = {
    restaurant: ['menuboard', 'pricePromo'],
    cafe: ['menuboard', 'pricePromo'],
    municipality: ['prayerTimes', 'disasterAlert'],
    hospital: ['queueBoard', 'prayerTimes', 'disasterAlert'],
    retail: ['pricePromo'],
    hotel: ['hotelGuest'],
    generic: [],
};
export function isSector(value) {
    return value in SECTOR_LABELS;
}
/** Normalise any stored sector string (legacy free-text → 'generic'). */
export function normalizeSector(value) {
    return value && isSector(value) ? value : 'generic';
}
/**
 * The modules an org may actually use: its sector preset, then super-admin
 * overrides applied on top. Used by the panel (nav/widgets) AND the API
 * (endpoint gating) so UI and server never disagree.
 */
export function effectiveModules(sector, overrides) {
    const set = new Set(SECTOR_MODULES[normalizeSector(sector)]);
    if (overrides) {
        for (const key of Object.keys(overrides)) {
            if (overrides[key])
                set.add(key);
            else
                set.delete(key);
        }
    }
    return [...set];
}
export function hasModule(module, sector, overrides) {
    return effectiveModules(sector, overrides).includes(module);
}
//# sourceMappingURL=types.js.map